import React, { useEffect, useState } from "react";
import { ChevronLeft, RotateCw } from "lucide-react";
import MatchCard from "../components/ui/MatchCard";
import TournamentResultModal from "../components/modals/TournamentResultModal";
import { useTournaments } from "../hooks";

interface TournamentBracketViewProps {
  tournamentName: string;
  eventId: string;
  onBack: () => void;
  isAdmin: boolean;
  isEventEnded: boolean;
}

const TournamentBracketView: React.FC<TournamentBracketViewProps> = ({
  tournamentName,
  eventId,
  onBack,
  isAdmin,
  isEventEnded,
}) => {
  const {
    matches,
    isLoading,
    error,
    fetchTournament,
    registerTournamentResult,
    updateTournamentResult,
  } = useTournaments();
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    round?: number;
    blockNumber?: number;
    p1TournamentId?: string;
    p2TournamentId?: string;
    p1Name?: string;
    p2Name?: string;
    p1Wins?: number | null;
    p2Wins?: number | null;
  }>({ isOpen: false });
  const [isReloading, setIsReloading] = useState(false);
  const CARD_HEIGHT = 120;
  const BASE_GAP = 24;
  const COLUMN_GAP = 56;
  const CONNECTOR_WIDTH = COLUMN_GAP / 2;
  const canRegisterResult = isAdmin && !isEventEnded;
  const roundColumns = matches.reduce<Record<number, typeof matches>>(
    (acc, match) => {
      if (!acc[match.round]) acc[match.round] = [];
      acc[match.round].push(match);
      return acc;
    },
    {},
  );

  const rounds = Object.keys(roundColumns)
    .map(Number)
    .sort((a, b) => b - a);

  const getExpectedMatchCount = (round: number): number =>
    Math.max(1, Math.floor(round / 2));

  const getLayoutMatchCount = (round: number): number =>
    Math.max(roundColumns[round]?.length ?? 0, getExpectedMatchCount(round));

  const normalizePx = (value: number): number => Math.round(value);

  const getCellLaneState = (
    p1Wins: number | null,
    p2Wins: number | null,
  ): {
    p1: "winner" | "loser" | "pending";
    p2: "winner" | "loser" | "pending";
  } => {
    if (p1Wins === null || p2Wins === null || p1Wins === p2Wins) {
      return { p1: "pending", p2: "pending" };
    }
    if (p1Wins > p2Wins) {
      return { p1: "winner", p2: "loser" };
    }
    return { p1: "loser", p2: "winner" };
  };

  const getAdvanceFillClass = (
    p1Wins: number | null,
    p2Wins: number | null,
  ): string => {
    if (p1Wins === null || p2Wins === null || p1Wins === p2Wins) {
      return "bg-slate-300";
    }
    return "bg-amber-400";
  };

  const getFeederClass = (feeder: (typeof matches)[0] | undefined): string => {
    if (
      !feeder ||
      feeder.p1Wins === null ||
      feeder.p2Wins === null ||
      feeder.p1Wins === feeder.p2Wins
    ) {
      return "border-slate-300 border-dashed border-[2px]";
    }
    return "border-amber-400 border-[2px]";
  };

  const getLaneFillClass = (state: "winner" | "loser" | "pending"): string => {
    return state === "winner" ? "bg-amber-400" : "bg-slate-300";
  };

  const roundLayouts = rounds.reduce<
    Record<number, { gap: number; marginTop: number; step: number }>
  >((acc, round, index) => {
    if (index === 0) {
      acc[round] = {
        gap: BASE_GAP,
        marginTop: 0,
        step: CARD_HEIGHT + BASE_GAP,
      };
      return acc;
    }

    const prevRound = rounds[index - 1];
    const prevCount = getLayoutMatchCount(prevRound);
    const currentCount = getLayoutMatchCount(round);
    const ratio = Math.max(1, prevCount / currentCount);
    const prevStep = acc[prevRound].step;
    const currentStep = normalizePx(prevStep * ratio);
    const currentMarginTop = normalizePx(
      acc[prevRound].marginTop + (currentStep - prevStep) / 2,
    );

    acc[round] = {
      gap: normalizePx(Math.max(0, currentStep - CARD_HEIGHT)),
      marginTop: currentMarginTop,
      step: currentStep,
    };

    return acc;
  }, {});

  const getRoundLabel = (round: number): string => {
    if (round === 4) return "Semifinal";
    if (round === 2) return "Final";
    return `Round ${round}`;
  };

  useEffect(() => {
    fetchTournament(eventId);
  }, [eventId, fetchTournament]);

  useEffect(() => {
    if (isEventEnded) {
      setModalState({ isOpen: false });
    }
  }, [isEventEnded]);

  const handleReload = async () => {
    setIsReloading(true);
    await fetchTournament(eventId, { nocache: true });
    setIsReloading(false);
  };

  return (
    <div className="flex flex-col h-full min-h-viewport bg-[#f8fafc] animate-in slide-in-from-right-8 duration-500 overflow-hidden">
      <header className="px-6 pt-[calc(env(safe-area-inset-top)+0.75rem)] pb-3 flex items-center justify-between border-b border-slate-100 bg-white/80 backdrop-blur-md sticky top-0 z-20 text-left shrink-0">
        <div className="flex items-center gap-4 text-left min-w-0 w-full">
          <button
            onClick={onBack}
            className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center bg-white shadow-sm active:scale-90"
          >
            <ChevronLeft className="w-5 h-5 text-slate-600" />
          </button>
          <h2 className="flex-1 min-w-0 font-black text-slate-900 uppercase text-xs tracking-wide text-left line-clamp-2 leading-snug">
            {tournamentName}
          </h2>
          <button
            onClick={handleReload}
            disabled={isReloading}
            className="group w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center bg-white shadow-sm active:scale-90 active:shadow-inner transition-all duration-150 disabled:opacity-50"
          >
            <RotateCw
              className={`w-5 h-5 text-slate-600 transition-transform duration-150 group-active:scale-90 group-active:-rotate-12 ${
                isReloading ? "animate-spin" : ""
              }`}
            />
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-x-auto flex items-start gap-12 px-8 py-12 scrollbar-hide pb-40">
        {isLoading && (
          <p className="text-slate-400 text-sm m-auto">読み込み中...</p>
        )}
        {error && <p className="text-rose-400 text-sm m-auto">{error}</p>}
        {!isLoading && !error && matches.length === 0 && (
          <p className="text-slate-300 text-sm m-auto">
            試合データがありません
          </p>
        )}
        {!isLoading && !error && matches.length > 0 && (
          <div
            className="flex items-start shrink-0 text-left"
            style={{ gap: `${COLUMN_GAP}px` }}
          >
            {(() => {
              const finalMatch = matches.find((m) => m.round === 2);
              const hasFinalResult =
                finalMatch != null &&
                finalMatch.p1Wins !== null &&
                finalMatch.p2Wins !== null;
              return (
                <>
                  {rounds.map((round, roundIndex) => (
                    <div
                      key={round}
                      className="flex flex-col gap-3 shrink-0 relative"
                    >
                      {round === 2 ? (
                        <p className="text-[11px] font-black uppercase tracking-widest pl-1 text-amber-500 flex items-center gap-1">
                          🏆 {getRoundLabel(round)}
                        </p>
                      ) : (
                        <p className="text-[10px] font-black uppercase tracking-widest pl-1 text-slate-400">
                          {getRoundLabel(round)}
                        </p>
                      )}
                      <div
                        className="flex flex-col relative"
                        style={{
                          gap: `${roundLayouts[round]?.gap ?? BASE_GAP}px`,
                          marginTop: `${roundLayouts[round]?.marginTop ?? 0}px`,
                        }}
                      >
                        {Array.from(
                          { length: getExpectedMatchCount(round) },
                          (_, slotIndex) => {
                            const match = roundColumns[round].find(
                              (m) => m.blockNumber === slotIndex + 1,
                            );
                            const hasNextRound = roundIndex < rounds.length - 1;

                            if (!match) {
                              return (
                                <div
                                  key={`${round}-slot-${slotIndex}`}
                                  className="relative"
                                  style={{ height: `${CARD_HEIGHT}px` }}
                                />
                              );
                            }

                            const laneState = getCellLaneState(
                              match.p1Wins,
                              match.p2Wins,
                            );
                            const rightTopLaneFill = getLaneFillClass(
                              laneState.p1,
                            );
                            const rightBottomLaneFill = getLaneFillClass(
                              laneState.p2,
                            );
                            const advanceFill = getAdvanceFillClass(
                              match.p1Wins,
                              match.p2Wins,
                            );

                            // 前ラウンドでこのカードのp1/p2に進出してきた試合
                            const prevRound =
                              roundIndex > 0 ? rounds[roundIndex - 1] : null;
                            const feederP1 =
                              prevRound !== null
                                ? (roundColumns[prevRound] ?? []).find(
                                    (m) => m.blockNumber === slotIndex * 2 + 1,
                                  )
                                : undefined;
                            const feederP2 =
                              prevRound !== null
                                ? (roundColumns[prevRound] ?? []).find(
                                    (m) => m.blockNumber === slotIndex * 2 + 2,
                                  )
                                : undefined;
                            const feederP1Class = getFeederClass(feederP1);
                            const feederP2Class = getFeederClass(feederP2);
                            const currentRoundLayout = roundLayouts[round];
                            const previousRoundLayout =
                              prevRound !== null
                                ? roundLayouts[prevRound]
                                : null;
                            const currentCardTop =
                              (currentRoundLayout?.marginTop ?? 0) +
                              slotIndex *
                                (CARD_HEIGHT +
                                  (currentRoundLayout?.gap ?? BASE_GAP));
                            const p1EntryAbsoluteY =
                              currentCardTop + CARD_HEIGHT * 0.25;
                            const p2EntryAbsoluteY =
                              currentCardTop + CARD_HEIGHT * 0.75;
                            const getPreviousCardCenterY = (
                              previousSlotIndex: number,
                            ): number | null => {
                              if (!previousRoundLayout) return null;
                              const previousCardTop =
                                previousRoundLayout.marginTop +
                                previousSlotIndex *
                                  (CARD_HEIGHT + previousRoundLayout.gap);
                              return previousCardTop + CARD_HEIGHT / 2;
                            };
                            const feederP1SourceY =
                              roundIndex > 0
                                ? getPreviousCardCenterY(slotIndex * 2)
                                : null;
                            const feederP2SourceY =
                              roundIndex > 0
                                ? getPreviousCardCenterY(slotIndex * 2 + 1)
                                : null;
                            const feederP1VerticalTop =
                              feederP1SourceY === null
                                ? 0
                                : Math.min(feederP1SourceY, p1EntryAbsoluteY) -
                                  currentCardTop;
                            const feederP1VerticalHeight =
                              feederP1SourceY === null
                                ? 0
                                : Math.abs(feederP1SourceY - p1EntryAbsoluteY);
                            const feederP2VerticalTop =
                              feederP2SourceY === null
                                ? 0
                                : Math.min(feederP2SourceY, p2EntryAbsoluteY) -
                                  currentCardTop;
                            const feederP2VerticalHeight =
                              feederP2SourceY === null
                                ? 0
                                : Math.abs(feederP2SourceY - p2EntryAbsoluteY);

                            return (
                              <div
                                key={`${match.round}-${match.blockNumber}`}
                                className="relative"
                              >
                                <MatchCard
                                  p1={match.p1Name}
                                  p2={match.p2Name}
                                  p1LoginId={match.p1LoginId}
                                  p2LoginId={match.p2LoginId}
                                  s1={match.p1Wins}
                                  s2={match.p2Wins}
                                  isLive={false}
                                  matchNum={match.blockNumber}
                                  isAdmin={canRegisterResult}
                                  isFinalRound={round === 2}
                                  round={match.round}
                                  blockNumber={match.blockNumber}
                                  onResultClick={(
                                    round,
                                    blockNumber,
                                    p1Name,
                                    p2Name,
                                    p1Wins,
                                    p2Wins,
                                  ) => {
                                    if (!canRegisterResult) return;
                                    setModalState({
                                      isOpen: true,
                                      round,
                                      blockNumber,
                                      p1TournamentId: match.p1TournamentId,
                                      p2TournamentId: match.p2TournamentId,
                                      p1Name,
                                      p2Name,
                                      p1Wins,
                                      p2Wins,
                                    });
                                  }}
                                />

                                {hasNextRound && (
                                  <>
                                    {/* p1 セル線（カード右端から半分だけ伸ばす） */}
                                    <div
                                      className={`absolute h-[2px] ${rightTopLaneFill}`}
                                      style={{
                                        top: "calc(25% - 1px)",
                                        left: "100%",
                                        width: `${CONNECTOR_WIDTH / 2 + 1}px`,
                                      }}
                                    />
                                    {/* p2 セル線（カード右端から半分だけ伸ばす） */}
                                    <div
                                      className={`absolute h-[2px] ${rightBottomLaneFill}`}
                                      style={{
                                        top: "calc(75% - 1px)",
                                        left: "100%",
                                        width: `${CONNECTOR_WIDTH / 2 + 1}px`,
                                      }}
                                    />
                                    {/* 合流の短い縦線（p1線とp2線を繋ぐ） */}
                                    <div
                                      className={`absolute w-[2px] ${rightTopLaneFill}`}
                                      style={{
                                        top: "calc(25% - 1px)",
                                        left: `calc(100% + ${CONNECTOR_WIDTH / 2 - 1}px)`,
                                        height: "calc(25% + 2px)",
                                      }}
                                    />
                                    <div
                                      className={`absolute w-[2px] ${rightBottomLaneFill}`}
                                      style={{
                                        top: "calc(50% - 1px)",
                                        left: `calc(100% + ${CONNECTOR_WIDTH / 2 - 1}px)`,
                                        height: "calc(25% + 2px)",
                                      }}
                                    />
                                    {/* 勝者進出線（合流点から幹線まで） */}
                                    <div
                                      className={`absolute h-[2px] ${advanceFill}`}
                                      style={{
                                        top: "calc(50% - 1px)",
                                        left: `calc(100% + ${CONNECTOR_WIDTH / 2 - 1}px)`,
                                        width: `${CONNECTOR_WIDTH / 2 + 3}px`,
                                      }}
                                    />
                                  </>
                                )}

                                {roundIndex > 0 && (
                                  <>
                                    {/* p1 縦接続（前ラウンド勝者中心 -> 次ラウンド p1セル） */}
                                    {feederP1SourceY !== null && (
                                      <div
                                        className={`absolute border-r ${feederP1Class}`}
                                        style={{
                                          left: `${-CONNECTOR_WIDTH}px`,
                                          top: `${feederP1VerticalTop}px`,
                                          height: `${feederP1VerticalHeight}px`,
                                        }}
                                      />
                                    )}
                                    {/* p1入口線（前ラウンドisPairStart勝者が進出） */}
                                    <div
                                      className={`absolute border-t ${feederP1Class}`}
                                      style={{
                                        top: "calc(25% - 1px)",
                                        left: `${-CONNECTOR_WIDTH}px`,
                                        width: `${CONNECTOR_WIDTH}px`,
                                      }}
                                    />
                                    {/* p2 縦接続（前ラウンド勝者中心 -> 次ラウンド p2セル） */}
                                    {feederP2SourceY !== null && (
                                      <div
                                        className={`absolute border-r ${feederP2Class}`}
                                        style={{
                                          left: `${-CONNECTOR_WIDTH}px`,
                                          top: `${feederP2VerticalTop}px`,
                                          height: `${feederP2VerticalHeight}px`,
                                        }}
                                      />
                                    )}
                                    {/* p2入口線（前ラウンドisPairEnd勝者が進出） */}
                                    <div
                                      className={`absolute border-t ${feederP2Class}`}
                                      style={{
                                        top: "calc(75% - 1px)",
                                        left: `${-CONNECTOR_WIDTH}px`,
                                        width: `${CONNECTOR_WIDTH}px`,
                                      }}
                                    />
                                  </>
                                )}
                              </div>
                            );
                          },
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Results column: 1位・2位 */}
                  {hasFinalResult &&
                    (() => {
                      const finalMatch = matches.find((m) => m.round === 2)!;
                      const p1Won =
                        (finalMatch.p1Wins ?? 0) > (finalMatch.p2Wins ?? 0);
                      const winner = p1Won
                        ? finalMatch.p1Name
                        : finalMatch.p2Name;
                      const runnerUp = p1Won
                        ? finalMatch.p2Name
                        : finalMatch.p1Name;
                      const finalLayout = roundLayouts[2];
                      const RESULT_CARD_HEIGHT = 68;
                      const resultsTotalHeight =
                        RESULT_CARD_HEIGHT * 2 + BASE_GAP;
                      const finalCardCenter =
                        (finalLayout?.marginTop ?? 0) + CARD_HEIGHT / 2;
                      const resultsMarginTop = Math.max(
                        0,
                        finalCardCenter - resultsTotalHeight / 2,
                      );
                      return (
                        <div className="flex flex-col gap-3 shrink-0 relative">
                          <p className="text-[11px] font-black uppercase tracking-widest pl-1 text-amber-500">
                            順位
                          </p>
                          <div
                            className="flex flex-col relative"
                            style={{
                              gap: `${BASE_GAP}px`,
                              marginTop: `${resultsMarginTop}px`,
                            }}
                          >
                            {/* 1位 */}
                            <div
                              className="rounded-lg bg-gradient-to-br from-amber-100 via-yellow-50 to-orange-50 border-2 border-amber-400 px-4 flex items-center gap-3 shadow-md"
                              style={{
                                width: "208px",
                                height: `${RESULT_CARD_HEIGHT}px`,
                              }}
                            >
                              <span className="text-3xl leading-none select-none">
                                🥇
                              </span>
                              <div className="min-w-0">
                                <p className="text-[9px] font-black uppercase tracking-widest text-amber-500 mb-0.5">
                                  1位
                                </p>
                                <p className="text-sm font-black text-amber-800 truncate">
                                  {winner}
                                </p>
                              </div>
                            </div>

                            {/* 2位 */}
                            <div
                              className="rounded-lg bg-gradient-to-br from-slate-100 to-slate-50 border-2 border-slate-300 px-4 flex items-center gap-3 shadow-sm"
                              style={{
                                width: "208px",
                                height: `${RESULT_CARD_HEIGHT}px`,
                              }}
                            >
                              <span className="text-3xl leading-none select-none">
                                🥈
                              </span>
                              <div className="min-w-0">
                                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-0.5">
                                  2位
                                </p>
                                <p className="text-sm font-black text-slate-600 truncate">
                                  {runnerUp}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                </>
              );
            })()}
          </div>
        )}
      </div>

      {/* Tournament Result Modal */}
      {canRegisterResult && (
        <TournamentResultModal
          isOpen={modalState.isOpen}
          onClose={() => setModalState({ isOpen: false })}
          p1Name={modalState.p1Name ?? ""}
          p2Name={modalState.p2Name ?? ""}
          p1Wins={modalState.p1Wins ?? null}
          p2Wins={modalState.p2Wins ?? null}
          isUpdate={modalState.p1Wins !== null && modalState.p2Wins !== null}
          onSubmit={async (p1Wins, p2Wins) => {
            if (!modalState.p1TournamentId || !modalState.p2TournamentId) {
              console.error("Tournament IDs not found for selected match");
              return;
            }
            const isUpdate =
              modalState.p1Wins !== null && modalState.p2Wins !== null;
            if (isUpdate) {
              await updateTournamentResult(
                eventId,
                modalState.p1TournamentId,
                modalState.p2TournamentId,
                p1Wins,
                p2Wins,
              );
            } else {
              await registerTournamentResult(
                eventId,
                modalState.p1TournamentId,
                modalState.p2TournamentId,
                p1Wins,
                p2Wins,
              );
            }
          }}
        />
      )}
    </div>
  );
};

export default TournamentBracketView;
